<script setup>
import { ref, computed } from "vue";
import closeIcon from "@/assets/icon/close.svg";
import { setLocalStorage, getLocalStorage, appHapticFeedback } from "@/utils/common";
import { useConfigStore } from "@/store/config";
import { useUserStore } from "@/store/user";
import { useI18n } from "vue-i18n";
import { paymentSubmit } from "@/api/TON";

const { t } = useI18n();
const config = useConfigStore();
const user_store = useUserStore();
const wallAddress = ref(null);

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const onClose = () => {
  emit("close");
  appHapticFeedback();
};

const save = async () => {
  appHapticFeedback();
  if (wallAddress.value == "" || wallAddress.value == null) {
    return;
  }
  setLocalStorage("withdraw_address", wallAddress.value);
  user_store.setPaymentInfo(wallAddress.value, config.withdraw_mode);
  await paymentSubmit({
    payment_type: config.withdraw_mode,
    payment_info: wallAddress.value
  });
  wallAddress.value = "";
  onClose();
};
</script>

<template>
  <div class="wallet-config" v-show="props.show">
    <div class="wallet-config__container">
      <!-- Header -->
      <header class="wallet-config__header">
        <h2 class="wallet-config__title">{{ $t("message.Receivingwallet") }}</h2>
        <button class="wallet-config__close" @click="onClose">
          <img :src="closeIcon" alt="close" />
        </button>
      </header>

      <!-- Manual Input Section -->
      <section class="wallet-config__section">
        <div class="wallet-config__input-group">
          <textarea class="wallet-config__input"
            :placeholder="$t('message.Enterwalletaddress', { name: config.withdraw_chain })" v-model="wallAddress" />
        </div>
      </section>

      <!-- Save Button -->
      <button class="wallet-config__button wallet-config__button--save" @click="save">
        {{ $t("message.save") }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/surfaces" as surface;

.wallet-config {
  @include surface.dialog-overlay;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__container {
    @include surface.dialog-panel;
    background: #1e1e1e;
    border: 1px solid #2d2d2d;
  }

  &__header {
    @include surface.dialog-header;
    margin-bottom: 8px;
    position: relative;
  }

  &__title {
    @include surface.title;
    font-weight: bold;
    color: #ffffff;
  }

  &__close {
    @include surface.close-button;
    position: static;
    top: auto;
    right: auto;
    transform: none;
    border: none;
    background: none;

    img {
      width: 100%;
      height: 100%;
      filter: invert(1);
    }
  }

  &__section {
    margin-top: 8px;
  }

  &__label {
    color: #a0a0a0;
    font-size: 16px;
    margin: 0 0 8px;
    font-weight: normal;
  }

  &__input-group {
    position: relative;
    margin-bottom: 8px;
  }

  &__input {
    height: 72px;
    padding: 8px;
    border-radius: 10px;
    line-height: 1.5;
    resize: vertical;
    width: 100%;
    border: 1px solid #2d2d2d;
    font-size: 16px;
    box-sizing: border-box;
    background-color: #2d2d2d;
    color: #ffffff;

    &::placeholder {
      color: #a0a0a0;
    }

    &:focus {
      outline: none;
      border-color: v-bind("config.themes_color");
    }
  }

  &__tip {
    font-size: 13px;
    line-height: 1.5;
    color: #a0a0a0;
  }

  &__paste {
    @include surface.press-feedback($rest-transform: translateY(-50%));
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    padding: 8px 12px;
    background-color: #2d2d2d;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    color: #ffffff;
    cursor: pointer;
  }

  &__button {
    @include surface.button;
    width: 100%;
    border: none;
    font-weight: bold;
    color: #000;
    cursor: pointer;
    background: linear-gradient(45deg,
        var(--theme-color) 0%,
        rgba(var(--theme-color-rgb, 0, 255, 157), 0.9) 100%);

    svg {
      width: 24px;
      height: 24px;
      margin-right: 6px;
    }

    &--connect {
      margin-top: 8px;
      background-color: #2d2d2d;
    }

    &--save {
      margin-top: 8px;
    }
  }
}
.wallet-config__close img {
  width: 24px;
  height: 24px;
}
</style>
